import React, {useEffect} from "react";
import Button from "../../presentational/Button";
import Input from "../../presentational/Input";
import LocalizedText from "../../presentational/LocalizedText";
import useTranslate from "../../../hooks/useTranslate";
import { isValidVerificationCode } from '../utils'
import { useResendCodeTimer } from "./hooks";
import { useTurnstile } from "../../../hooks/useTurnstile";

interface ForgotPasswordProps {
  emailOrPhone: string;
  verificationCode: string;
  serverError: string;
  isLoading: boolean;
  resendWait: number;
  resendPasswordResetCode: () => Promise<void>;
  setVerificationCode: (verificationCode: string) => void;
  verifyPhoneForPasswordReset: (verificationCode: string) => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  emailOrPhone,
  verificationCode,
  serverError,
  isLoading,
  resendWait,
  resendPasswordResetCode,
  setVerificationCode,
  verifyPhoneForPasswordReset,
}) => {
  const {
    onResendCode,
    waitCountdown,
    isCodeResent,
    isResendCodeReady,
  } = useResendCodeTimer(resendPasswordResetCode, resendWait)

  const translations = useTranslate(["participant-login-verification-code-label", "participant-login-next-label"])
  useTurnstile();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    verifyPhoneForPasswordReset(verificationCode)
    return false
  }

  return (
    <form className="login-action" onSubmit={handleSubmit}>
      <h1>
        <LocalizedText textKey="participant-login-forgot-password-title" />
      </h1>
      <p>
        <LocalizedText textKey="participant-login-forgot-password-info" values={{ emailOrPhone }} />
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <div className="field-group">
        <label htmlFor="verificationCode" className="login-field">{translations["participant-login-verification-code-label"]}</label>
        <Input
          autoComplete="one-time-code"
          autoFocus={true}
          className="autofocus"
          disabled={isLoading}
          value={verificationCode || ''}
          onChange={(e) => setVerificationCode(e.target.value)}
          id="verificationCode"
          type="tel"
          placeholder={"123456"}
          fullWidth
          htmlAttributes={{
            pattern: "[0-9]{6}",
            maxLength: 6,
            spellCheck: false,
            autoCorrect: "off",
            autoCapitalize: "none",
          }}
        />
      </div>
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          onClick={handleSubmit}
          disabled={!verificationCode || !isValidVerificationCode(verificationCode)}
          className="primary-action"
          width="100%"
          loading={isLoading}
          intent='primary'
          type="submit"
        >
          <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
        </Button>
      </div>
      <div aria-live="polite">
        {isResendCodeReady && (
          <p className="sub-action">
            <Button onClick={onResendCode} intent='primary' disabled={isLoading}>
              <LocalizedText textKey="participant-login-resend-code-label" />
            </Button>
          </p>
        )}
        {
          isCodeResent && !isResendCodeReady && (
            <p className="sub-action">
              <LocalizedText textKey="participant-login-verify-code-resent" />
            </p>
          )
        }
        {
          !isResendCodeReady && (
            <p className="sub-action">
              <LocalizedText textKey="participant-login-verify-resend-available-format" values={{ resendWait: waitCountdown }} />
            </p>
          )
        }
      </div>
    </form>
  )
}

export default ForgotPassword